import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { LabeledValue } from '../../../../shared/interfaces';
import { TIMELINE_SIMULATOR_FORM2_CONSTANTS } from '../models/timeline-criteria-form2-constants';

@Injectable()
export class SimulatorCriteriaForm2CascadeService {
  readonly TimelineCampaignTypes = TIMELINE_SIMULATOR_FORM2_CONSTANTS.TIMELINE_CAMPAIGN_TYPES;
  readonly TimelineProductLaunchTypes = TIMELINE_SIMULATOR_FORM2_CONSTANTS.TIMELINE_PRODUCT_LAUNCH_TYPES;
  readonly CampaignTypes = TIMELINE_SIMULATOR_FORM2_CONSTANTS.CAMPAIGN_TYPES;
  readonly ComplexityLevels = TIMELINE_SIMULATOR_FORM2_CONSTANTS.COMPLEXITY_LEVELS;
  readonly PosmValues = TIMELINE_SIMULATOR_FORM2_CONSTANTS.POSM_VALUES;
  readonly HallmarksTypes = TIMELINE_SIMULATOR_FORM2_CONSTANTS.HALLMARKS_TYPES;
  readonly HallmarksTypeTexts = TIMELINE_SIMULATOR_FORM2_CONSTANTS.HALLMARKS_TYPE_TEXTS;
  readonly CollectorTypes = TIMELINE_SIMULATOR_FORM2_CONSTANTS.COLLECTOR_TYPES;

  readonly ProductLaunchCampaignTypes = [
    this.CampaignTypes.STANDARD,
    this.CampaignTypes.NEW_TERRITORY,
    this.CampaignTypes.NONE,
  ];

  readonly DefaultPosmValues = [this.PosmValues.DMI_PRODUCED, this.PosmValues.LOCALLY_PRODUCED, this.PosmValues.NO];

  readonly ProductLaunchTypeDependency: Record<string, Record<string, string[]>> = {
    [this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS]: {
      complexity: [
        this.ComplexityLevels.ONE_TWO_T,
        this.ComplexityLevels.THREE_FOUR_T,
        this.ComplexityLevels.THREE_T,
        this.ComplexityLevels.FOUR_T,
      ],
      campaignTypes: this.ProductLaunchCampaignTypes,
    },
    [this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS]: {
      complexity: [this.ComplexityLevels.ONE_TWO_T, this.ComplexityLevels.THREE_T, this.ComplexityLevels.FOUR_T],
      campaignTypes: this.ProductLaunchCampaignTypes,
    },
    [this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS_NANO]: {
      complexity: [this.ComplexityLevels.ONE_TWO_T, this.ComplexityLevels.THREE_T, this.ComplexityLevels.FOUR_T],
      campaignTypes: this.ProductLaunchCampaignTypes,
    },
    [this.TimelineProductLaunchTypes.NOT_IN_CHINA]: {
      complexity: [
        this.ComplexityLevels.ONE_TWO_T,
        this.ComplexityLevels.THREE_FOUR_T,
        this.ComplexityLevels.THREE_T,
        this.ComplexityLevels.FOUR_T,
      ],
      campaignTypes: this.ProductLaunchCampaignTypes,
    },
    [this.TimelineProductLaunchTypes.NONE]: {
      complexity: [],
      campaignTypes: [
        this.CampaignTypes.STANDARD,
        this.CampaignTypes.NEW_TERRITORY,
        this.CampaignTypes.NONE,
        this.CampaignTypes.REDDIT,
      ],
    },
  };

  readonly CampaignTypeDependency: Record<string, Record<string, string[]>> = {
    [this.CampaignTypes.STANDARD]: {
      posm: this.DefaultPosmValues,
    },
    [this.CampaignTypes.NEW_TERRITORY]: {
      posm: this.DefaultPosmValues,
    },
    [this.CampaignTypes.NONE]: {
      posm: [],
    },
    [this.CampaignTypes.REDDIT]: {
      posm: [],
    },
  };

  readonly HallmarksTypeDependency: Record<string, Record<string, string[]>> = {
    [this.HallmarksTypes.GLOBAL_HALLMARKS]: {
      collector: [this.CollectorTypes.INCLUDE_FOR_CHINA, this.CollectorTypes.NOT_FOR_CHINA, this.CollectorTypes.NO],
      posm: this.DefaultPosmValues,
      set: this.DefaultPosmValues,
    },
    [this.HallmarksTypes.HOLIDAY]: {
      collector: [this.CollectorTypes.INCLUDE_FOR_CHINA, this.CollectorTypes.NOT_FOR_CHINA],
      posm: [this.PosmValues.DMI_PRODUCED],
      set: [this.PosmValues.DMI_PRODUCED],
    },
    [this.HallmarksTypes.CHINA_SPECIFIC_HALLMARKS]: {
      collector: [this.CollectorTypes.YES, this.CollectorTypes.NO],
      posm: [this.PosmValues.LOCALLY_PRODUCED, this.PosmValues.NO],
      set: this.DefaultPosmValues,
    },
  };

  readonly CollectorComplexityDependency: Record<string, string[]> = {
    [this.CollectorTypes.INCLUDE_FOR_CHINA]: [this.ComplexityLevels.ONE_TWO_T, this.ComplexityLevels.THREE_T],
    [this.CollectorTypes.NOT_FOR_CHINA]: [this.ComplexityLevels.ONE_TWO_T, this.ComplexityLevels.THREE_T],
    [this.CollectorTypes.YES]: [this.ComplexityLevels.ONE_TWO_T, this.ComplexityLevels.THREE_T],
    [this.CollectorTypes.NO]: [],
  };

  getAvailableProductLaunchTypes(): LabeledValue<string>[] {
    return [
      {
        text: this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS,
        value: this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS,
      },
      {
        text: this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS,
        value: this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS,
      },
      {
        text: this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS_NANO,
        value: this.TimelineProductLaunchTypes.CHINA_SPECIAL_COSMETICS_NANO,
      },
      {
        text: this.TimelineProductLaunchTypes.NOT_IN_CHINA,
        value: this.TimelineProductLaunchTypes.NOT_IN_CHINA,
      },
      {
        text: this.TimelineProductLaunchTypes.NONE,
        value: this.TimelineProductLaunchTypes.NONE,
      },
    ];
  }

  getAvailableHallmarksTypes(): LabeledValue<string>[] {
    return Object.keys(this.HallmarksTypes).map((key) => ({
      text: this.HallmarksTypeTexts[this.HallmarksTypes[key]],
      value: this.HallmarksTypes[key],
    }));
  }

  handleProjectTypeSwitch(form: FormGroup, value: string): void {
    form.patchValue({
      projectType: value,
    });

    if (value === this.TimelineCampaignTypes.PRODUCT_CAMPAIGN) {
      this.handleProductLaunchTypeSwitch(form, this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS);
    } else {
      this.handleHallmarksTypeSwitch(form, this.HallmarksTypes.GLOBAL_HALLMARKS);
    }
  }

  handleProductLaunchTypeSwitch(form: FormGroup, value: string): void {
    const productLaunchType = this.ProductLaunchTypeDependency[value];

    const availableComplexities = productLaunchType.complexity.map((complexity) => ({
      text: complexity,
      value: complexity,
    }));

    const availableCampaignTypes = productLaunchType.campaignTypes.map((campaignType) => ({
      text: campaignType,
      value: campaignType,
    }));

    const firstCampaignType = availableCampaignTypes.length ? availableCampaignTypes[0].value : '';
    let availablePosm: string[] = [];

    if (firstCampaignType) {
      availablePosm = this.CampaignTypeDependency[firstCampaignType].posm;
    }

    form.patchValue({
      productLaunchType: value,
      complexity: availableComplexities.length ? availableComplexities[0].value : '',
      campaignType: firstCampaignType,
      posm: availablePosm.length ? availablePosm[0] : '',
    });
  }

  handleHallmarksTypeSwitch(form: FormGroup, value: string): void {
    const hallmarkDependencies = this.HallmarksTypeDependency[value];
    const availableCollectors = hallmarkDependencies.collector;
    const availablePosm = hallmarkDependencies.posm;
    const availableSets = hallmarkDependencies.set;

    const firstCollector = availableCollectors.length ? availableCollectors[0] : '';
    let availableCollectorComplexities: LabeledValue<string>[] = [];

    if (firstCollector) {
      availableCollectorComplexities = this.CollectorComplexityDependency[firstCollector].map((complexity) => ({
        text: complexity,
        value: complexity,
      }));
    }

    form.patchValue({
      hallmarksType: value,
      collectorType: firstCollector,
      collectorComplexity: availableCollectorComplexities.length ? availableCollectorComplexities[0].value : '',
      posm: availablePosm.length ? availablePosm[0] : '',
      set: availableSets.length ? availableSets[0] : '',
    });
  }

  handleCampaignTypeSwitch(form: FormGroup, value: string): void {
    const availablePosm = this.CampaignTypeDependency[value].posm;

    form.patchValue({
      campaignType: value,
      posm: availablePosm.length ? availablePosm[0] : '',
    });
  }

  handleCollectorTypeSwitch(form: FormGroup, value: string): void {
    const collectorComplexities = this.CollectorComplexityDependency[value];
    const availableCollectorComplexities = collectorComplexities.map((complexity) => ({
      text: complexity,
      value: complexity,
    }));

    form.patchValue({
      collectorType: value,
      collectorComplexity: availableCollectorComplexities.length ? availableCollectorComplexities[0].value : '',
    });
  }

  getAvailableComplexities(productLaunchType: string): LabeledValue<string>[] {
    const productLaunchTypeData = this.ProductLaunchTypeDependency[productLaunchType];
    return productLaunchTypeData.complexity.map((complexity) => ({
      text: complexity,
      value: complexity,
    }));
  }

  getAvailableCampaignTypes(productLaunchType: string): LabeledValue<string>[] {
    const productLaunchTypeData = this.ProductLaunchTypeDependency[productLaunchType];
    return productLaunchTypeData.campaignTypes.map((campaignType) => ({
      text: campaignType,
      value: campaignType,
    }));
  }

  getAvailablePosm(campaignType: string): string[] {
    return this.CampaignTypeDependency[campaignType].posm;
  }

  getAvailableCollectors(hallmarksType: string): string[] {
    return this.HallmarksTypeDependency[hallmarksType].collector;
  }

  getAvailablePosmForHallmarks(hallmarksType: string): string[] {
    return this.HallmarksTypeDependency[hallmarksType].posm;
  }

  getAvailableSets(hallmarksType: string): string[] {
    return this.HallmarksTypeDependency[hallmarksType].set;
  }

  getAvailableCollectorComplexities(collectorType: string): LabeledValue<string>[] {
    const collectorComplexities = this.CollectorComplexityDependency[collectorType];
    return collectorComplexities.map((complexity) => ({
      text: complexity,
      value: complexity,
    }));
  }
}
